<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog
      class="fixed inset-0"
      style="z-index: 100"
      @close="model = false"
    >
      <TransitionChild as="template" enter="ease-in-out duration-500" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-500" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/70 transition-opacity" />
      </TransitionChild>
      <div
        class="fixed inset-0 overflow-y-auto px-4 py-4 sm:px-6 lg:px-8 flex"
        :class="props.fullScroll ? 'items-start' : 'items-center'"
      >
        <TransitionChild as="template" enter="transform transition ease-in-out duration-200 sm:duration-200" enter-from="opacity-0" enter-to="opacity-1" leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="opacity-1" leave-to="opacity-0">
          <DialogPanel
            class="w-full relative mx-auto my-8 flex flex-col transform-gpu rounded-xl bg-gray-800 shadow-xl ring-zinc-900/7.5 dark:bg-zinc-900 dark:ring-zinc-800"
            :class="[sizeClass, props.fullScroll ? '' : 'max-h-[calc(100vh-6rem)]']"
          >
            <div class="shrink-0">
              <slot name="header">
                <b-modal-header
                  v-if="!props.noHeader || props.title"
                  :title="props.title!"
                  :description="props.description"
                  @close="model = false"
                />
              </slot>
            </div>

            <div v-if="!props.noClose" class="absolute right-8 top-8">
              <button @click="model = false" class="cursor-pointer relative rounded-md text-gray-300 hover:text-white focus:outline-none focus:none">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.9996 14.1216L17.3026 19.4246C17.584 19.706 17.9657 19.8641 18.3636 19.8641C18.7616 19.8641 19.1432 19.706 19.4246 19.4246C19.706 19.1432 19.8641 18.7616 19.8641 18.3636C19.8641 17.9657 19.706 17.584 19.4246 17.3026L14.1196 11.9996L19.4236 6.69662C19.5629 6.55729 19.6733 6.39189 19.7487 6.20987C19.824 6.02785 19.8628 5.83277 19.8627 5.63577C19.8627 5.43877 19.8238 5.24371 19.7484 5.06172C19.673 4.87974 19.5624 4.71439 19.4231 4.57512C19.2838 4.43586 19.1184 4.3254 18.9364 4.25005C18.7543 4.1747 18.5592 4.13595 18.3623 4.13599C18.1653 4.13604 17.9702 4.17489 17.7882 4.25032C17.6062 4.32575 17.4409 4.43629 17.3016 4.57562L11.9996 9.87862L6.6966 4.57562C6.5583 4.43229 6.39284 4.31794 6.20987 4.23924C6.0269 4.16055 5.83009 4.11907 5.63092 4.11725C5.43176 4.11543 5.23422 4.15329 5.04984 4.22862C4.86546 4.30395 4.69793 4.41526 4.55703 4.55603C4.41612 4.6968 4.30466 4.86422 4.22916 5.04853C4.15365 5.23284 4.1156 5.43034 4.11724 5.62951C4.11887 5.82868 4.16016 6.02553 4.23869 6.20857C4.31721 6.39161 4.43141 6.55718 4.5746 6.69562L9.8796 11.9996L4.5756 17.3036C4.43241 17.4421 4.31821 17.6076 4.23969 17.7907C4.16116 17.9737 4.11987 18.1706 4.11824 18.3697C4.1166 18.5689 4.15465 18.7664 4.23016 18.9507C4.30566 19.135 4.41712 19.3024 4.55803 19.4432C4.69893 19.584 4.86646 19.6953 5.05084 19.7706C5.23522 19.846 5.43276 19.8838 5.63192 19.882C5.83109 19.8802 6.0279 19.8387 6.21087 19.76C6.39384 19.6813 6.5593 19.5669 6.6976 19.4236L11.9996 14.1216Z" fill="#F2F2F2"/>
                </svg>
              </button>
            </div>

            <div :class="props.fullScroll ? '' : 'flex-1 min-h-0 overflow-y-auto'">
              <slot />
            </div>

            <div class="shrink-0">
              <slot name="footer">
                <b-modal-footer
                  v-if="!noFooter"
                  @add="onAdd"
                  @cancel="onCancel"
                  :loading="props.loading"
                />
              </slot>
            </div>

          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Dialog, DialogBackdrop, DialogPanel, TransitionRoot, TransitionChild } from "@headlessui/vue";
import BModalHeader from "~/components/modal/BModalHeader.vue";
import BModalFooter from "~/components/modal/BModalFooter.vue";

const model = defineModel<boolean>({ default: false })
const isOpen = computed(() => model.value === true)

interface Props {
  noHeader?: boolean;
  noFooter?: boolean;
  noClose?: boolean;
  title?: string;
  description?: string;
  loading?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  // Quand true, la modale entière défile (le panneau grandit avec son contenu et
  // l'overlay scrolle) au lieu de figer la hauteur et scroller seulement le corps.
  fullScroll?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'lg',
});

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'sm:max-w-sm lg:max-w-sm';
    case 'md':
      return 'sm:max-w-md lg:max-w-md';
    case 'lg':
      return 'sm:max-w-lg lg:max-w-lg';
    case 'xl':
      return 'sm:max-w-xl lg:max-w-xl';
    case '2xl':
      return 'sm:max-w-2xl lg:max-w-2xl';
    case '3xl':
      return 'sm:max-w-3xl lg:max-w-3xl';
    case '4xl':
      return 'sm:max-w-4xl lg:max-w-4xl';
    case '5xl':
      return 'sm:max-w-4xl lg:max-w-5xl';
    default:
      return 'sm:max-w-lg lg:max-w-lg';
  }
});

const emit = defineEmits(["cancel", "add"]);

const onAdd = (event) => {
  emit("add", event);
};
const onCancel = (event) => {
  emit("cancel", event);
  model.value = false;
};
</script>
